import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '../../lib/api-fetcher';
import type { AccountNode } from '../accounting/schemas';
import type { PaginatedResponse } from '@repo/types';

export interface PaymentStatusDetail {
  id: string;
  status: 'CREATED' | 'PENDING' | 'SUCCESS' | 'FAILED' | 'EXPIRED' | 'REFUNDED';
  amount: number;
  expiresAt: string;
  completedAt: string | null;
  providerRef: string | null;
  failureReason: string | null;
}

export function usePaymentStatus(paymentId: string) {
  return useQuery({
    queryKey: ['payment', 'status', paymentId],
    queryFn: () => fetcher.get<PaymentStatusDetail>(`/staff/v1/payments/${paymentId}/status`),
    refetchInterval: (query) => {
      const data = query?.state?.data as PaymentStatusDetail | undefined;
      return data?.status === 'CREATED' || data?.status === 'PENDING' ? 5000 : false;
    },
  });
}

export interface ReceiptVoucher {
  id: string;
  voucherNumber: string;
  // Matches the real Drizzle column names the GET list endpoint actually returns
  // (apps/api/src/staff/finance/finance.controller.ts's listReceiptVouchers does a raw
  // `.select()`, so the response shape is the DB column names, not a display-friendly alias).
  voucherDate: string;
  depositorName?: string;
  customerId?: string;
  category?: string;
  amountPaise: number;
  paymentMode: 'CASH' | 'CHEQUE' | 'UPI' | 'NEFT';
  referenceNumber?: string;
  notes?: string;
  status: 'DRAFT' | 'APPROVED' | 'CANCELLED';
  cancelReason?: string;
  recordedBy: string;
}

export function useReceiptVouchers(filters?: { query?: string; category?: string; paymentMode?: string; status?: string }) {
  return useQuery({
    queryKey: ['receipt-vouchers', filters],
    queryFn: () => {
      const params = filters
        ? Object.entries(filters)
            .filter(([, v]) => v !== undefined && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
            .join('&')
        : '';
      return fetcher.get<PaginatedResponse<ReceiptVoucher>>(`/staff/v1/finance/receipt-vouchers?${params}`);
    },
  });
}

export function useCreateReceiptVoucher() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => fetcher.post<ReceiptVoucher>('/staff/v1/finance/receipt-vouchers', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipt-vouchers'] });
    },
  });
}

export function useCancelReceiptVoucher() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      fetcher.post<ReceiptVoucher>(`/staff/v1/finance/receipt-vouchers/${id}/cancel`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipt-vouchers'] });
    },
  });
}

// Advances
export function useAdvancesList(filters?: Record<string, string | number | boolean | undefined>) {
  return useQuery({
    queryKey: ['advances', filters],
    queryFn: () => {
      const params = filters ? new URLSearchParams(filters as unknown as Record<string, string>).toString() : '';
      return fetcher.get<PaginatedResponse<unknown>>(`/staff/v1/finance/advances?${params}`);
    },
  });
}

export function useRecordAdvancePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, amountPaise, paymentMode, particulars, entryDate }: {
      id: string;
      amountPaise: number;
      paymentMode: 'CASH' | 'BANK_TRANSFER' | 'CHEQUE';
      particulars: string;
      entryDate: string;
    }) =>
      fetcher.post<unknown>(`/staff/v1/finance/advances/${id}/payments`, { amountPaise, paymentMode, particulars, entryDate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advances'] });
    },
  });
}

export function useCreateAdvance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      amountPaise: number;
      schemeId: string;
      subscriptionId: string;
      auctionId: string;
      interestRateAnnualBps?: number;
      prizedDuePaise?: number;
      estimatedPrizedPaise?: number;
      docChargePaise?: number;
      chitInterestPaise?: number;
      notes?: string;
      advanceDate?: string;
    }) => fetcher.post<unknown>('/staff/v1/finance/advances', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advances'] });
    },
  });
}

// Running Receipts
export function useRunningReceipts(filters?: Record<string, string | number | boolean | undefined>) {
  return useQuery({
    queryKey: ['running-receipts', filters],
    queryFn: () => {
      const params = filters ? new URLSearchParams(filters as unknown as Record<string, string>).toString() : '';
      return fetcher.get<PaginatedResponse<unknown>>(`/staff/v1/finance/running-receipts?${params}`);
    },
  });
}

export function useBounceRunningReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetcher.post<unknown>(`/staff/v1/finance/running-receipts/${id}/bounce`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['running-receipts'] });
    },
  });
}

export function useClearRunningReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetcher.post<unknown>(`/staff/v1/finance/running-receipts/${id}/clear`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['running-receipts'] });
    },
  });
}

export function useCreateRunningReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: unknown) => fetcher.post<unknown>(`/staff/v1/finance/running-receipts`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['running-receipts'] });
    },
  });
}

export function useCustomerGroupBalances(customerId?: string) {
  return useQuery({
    queryKey: ['customer-group-balances', customerId],
    queryFn: () => fetcher.get<{ data: Record<string, unknown>[] }>(`/staff/v1/finance/members/${customerId}/group-balances`),
    enabled: !!customerId,
  });
}

export function useCustomerPendingDues(customerId?: string) {
  return useQuery({
    queryKey: ['customer-pending-dues', customerId],
    queryFn: () => fetcher.get<{ data: Record<string, unknown>[] }>(`/staff/v1/finance/members/${customerId}/pending-dues`),
    enabled: !!customerId,
  });
}

export function useSubscriptionLateFees(subscriptionId?: string) {
  return useQuery({
    queryKey: ['subscription-late-fees', subscriptionId],
    queryFn: () => fetcher.get<{ data: Record<string, unknown>[] }>(`/staff/v1/finance/subscriptions/${subscriptionId}/late-fees`),
    enabled: !!subscriptionId,
  });
}

// Group Deposits
export function useGroupDeposits(filters?: Record<string, string | number | boolean | undefined>) {
  return useQuery({
    queryKey: ['group-deposits', filters],
    queryFn: () => {
      const params = filters ? new URLSearchParams(filters as unknown as Record<string, string>).toString() : '';
      return fetcher.get<PaginatedResponse<unknown>>(`/staff/v1/finance/group-deposits?${params}`);
    },
  });
}

export function useCreateGroupDeposit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => fetcher.post<unknown>('/staff/v1/finance/group-deposits', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-deposits'] });
    },
  });
}

export function useReleaseGroupDeposit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetcher.post<unknown>(`/staff/v1/finance/group-deposits/${id}/release`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-deposits'] });
    },
  });
}

// Decrees
export function useDecrees(filters?: Record<string, string | number | boolean | undefined>) {
  return useQuery({
    queryKey: ['decrees', filters],
    queryFn: () => {
      const params = filters ? new URLSearchParams(filters as unknown as Record<string, string>).toString() : '';
      return fetcher.get<PaginatedResponse<unknown>>(`/staff/v1/finance/decrees?${params}`);
    },
  });
}

export function useCreateDecree() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => fetcher.post<unknown>('/staff/v1/finance/decrees', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['decrees'] });
    },
  });
}

export function useUpdateDecreeStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      fetcher.post<unknown>(`/staff/v1/finance/decrees/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['decrees'] });
    },
  });
}

// Chit Act Fees
export function useChitActFees(filters?: Record<string, string | number | boolean | undefined>) {
  return useQuery({
    queryKey: ['chit-act-fees', filters],
    queryFn: () => {
      const params = filters ? new URLSearchParams(filters as unknown as Record<string, string>).toString() : '';
      return fetcher.get<PaginatedResponse<unknown>>(`/staff/v1/finance/chit-act-fees?${params}`);
    },
  });
}

export function useCreateChitActFee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => fetcher.post<unknown>('/staff/v1/finance/chit-act-fees', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chit-act-fees'] });
    },
  });
}

export function useApproveChitActFee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetcher.post<unknown>(`/staff/v1/finance/chit-act-fees/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chit-act-fees'] });
    },
  });
}

// Write Offs
export function useWriteOffs(filters?: Record<string, string | number | boolean | undefined>) {
  return useQuery({
    queryKey: ['write-offs', filters],
    queryFn: () => {
      const params = filters ? new URLSearchParams(filters as unknown as Record<string, string>).toString() : '';
      return fetcher.get<PaginatedResponse<unknown>>(`/staff/v1/finance/write-offs?${params}`);
    },
  });
}

export function useCreateWriteOff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => fetcher.post<unknown>('/staff/v1/finance/write-offs', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['write-offs'] });
    },
  });
}

export function useRecordWriteOffRecovery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, amountPaise }: { id: string; amountPaise: number }) =>
      fetcher.post<unknown>(`/staff/v1/finance/write-offs/${id}/recoveries`, { amountPaise }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['write-offs'] });
    },
  });
}

// Bank Instruments
export interface BankInstrumentAllocation {
  id: string;
  allocationType: 'LATE_FEE' | 'INSTALLMENT' | 'OTHERS';
  subscriptionId?: string | null;
  amountPaise: number;
}

export interface BankInstrument {
  id: string;
  entryNumber: number;
  customerId: string;
  agentId?: string | null;
  transactionDate: string;
  paymentType: 'GPAY' | 'NEFT' | 'RTGS' | 'CHEQUE' | 'UPI' | 'E-NACH';
  bankName?: string | null;
  branchName?: string | null;
  bankAccountNumber?: string | null;
  instrumentNumber?: string | null;
  totalAmountPaise: number;
  othersAmountPaise: number;
  status: 'RECEIVED' | 'DEPOSITED' | 'REALISED' | 'RETURNED' | 'MEMBER_RETURNED';
  depositDate?: string | null;
  collectionDate?: string | null;
  returnDate?: string | null;
  memberReturnDate?: string | null;
  sendSms: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useBankInstruments(filters?: Record<string, string | number | boolean | undefined>) {
  return useQuery({
    queryKey: ['bank-instruments', filters],
    queryFn: () => {
      const params = filters ? new URLSearchParams(filters as unknown as Record<string, string>).toString() : '';
      return fetcher.get<{ data: BankInstrument[]; meta: { total: number; page: number; limit: number } }>(`/staff/v1/collections/bank-instruments?${params}`);
    },
  });
}

export function useBankInstrument(id: string) {
  return useQuery({
    queryKey: ['bank-instrument', id],
    queryFn: () => fetcher.get<{ instrument: BankInstrument; allocations: BankInstrumentAllocation[] }>(`/staff/v1/collections/bank-instruments/${id}`),
    enabled: !!id,
  });
}

export function useCreateBankInstrument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => fetcher.post<BankInstrument>('/staff/v1/collections/bank-instruments', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-instruments'] });
    },
  });
}

export function useUpdateBankInstrumentStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, depositDate, collectionDate, returnDate, memberReturnDate }: { id: string; status: string; depositDate?: string; collectionDate?: string; returnDate?: string; memberReturnDate?: string }) =>
      fetcher.post<BankInstrument>(`/staff/v1/collections/bank-instruments/${id}/status`, { status, depositDate, collectionDate, returnDate, memberReturnDate }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['bank-instruments'] });
      queryClient.invalidateQueries({ queryKey: ['bank-instrument', id] });
    },
  });
}

export function useDeleteBankInstrument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetcher.delete<void>(`/staff/v1/collections/bank-instruments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-instruments'] });
    },
  });
}

function flattenAccounts(nodes: AccountNode[]): AccountNode[] {
  const result: AccountNode[] = [];
  for (const n of nodes) {
    result.push(n);
    if (n.children?.length) result.push(...flattenAccounts(n.children as AccountNode[]));
  }
  return result;
}

export function useAccountsList() {
  return useQuery({
    queryKey: ['accounts', 'list'],
    queryFn: async () => {
      const res = await fetcher.get<{ data: AccountNode[] } | AccountNode[]>('/staff/v1/accounts');
      const nodes = (res && typeof res === 'object' && 'data' in res && Array.isArray((res as { data: AccountNode[] }).data))
        ? (res as { data: AccountNode[] }).data
        : (Array.isArray(res) ? res : []);
      return flattenAccounts(nodes);
    },
  });
}

